import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from 'react'
import useWorkflowState from '../hooks/useWorkflowState'
import usePreviewSizing, { PREVIEW_SIZE_PRESETS } from '../hooks/usePreviewSizing'
import useRecommendationMapping from '../hooks/useRecommendationMapping'
import usePrevious from '../hooks/usePrevious'
import { useDataContext } from './DataContext'
import { useChartContext } from './ChartContext'

const UIContext = createContext(null)

export { PREVIEW_SIZE_PRESETS }

export function UIProvider({ children }) {
  const { data, userData, auditSummary, largeDatasetInfo, dataSource } = useDataContext()

  const {
    currentChart,
    mapping,
    visualOptions,
    setVisualOptions,
    handleChartChange,
    setMapping,
    dataMappingRef,
    activeRecommendation,
    setActiveRecommendation,
    importProject,
    setBrandTheme,
  } = useChartContext()

  const [currentStep, setCurrentStep] = useState('load')
  const [showIntro, setShowIntro] = useState(true)
  const [activeMapTab, setActiveMapTab] = useState('chart')
  const [activeStyleTab, setActiveStyleTab] = useState('brand')
  const [previewSizePreset, setPreviewSizePreset] = useState('widescreen')
  const [previewCustomSize, setPreviewCustomSize] = useState({
    width: '1600',
    height: '900',
  })
  const [templateLandingStep, setTemplateLandingStep] = useState(null)

  const prevData = usePrevious(data)

  const { mappingComplete, workflowSteps, canAdvance, goToNextStep, goToPreviousStep } =
    useWorkflowState({
      currentStep,
      setCurrentStep,
      data,
      currentChart,
      mapping,
    })

  const { previewRendererMode, previewDimensions } = usePreviewSizing({
    currentChart,
    data,
    mapping,
    previewSizePreset,
    previewCustomSize,
    largeDatasetInfo,
    setVisualOptions,
  })

  const { recommendations, aiResult, handleRecommendationSelect } =
    useRecommendationMapping({
      auditSummary,
      userData,
      currentChart,
      data,
      activeRecommendation,
      setActiveRecommendation,
      handleChartChange,
      setActiveMapTab,
      setCurrentStep,
      setMapping,
      dataMappingRef,
      sampleChartTypes: dataSource?.type === 'sample' ? (dataSource.chartTypes || []) : [],
    })

  useEffect(() => {
    if (!prevData && data) {
      setCurrentStep('load')
    }
  }, [data, prevData])

  useEffect(() => {
    if (templateLandingStep === 'style' && data && currentChart) {
      setActiveStyleTab('brand')
      setCurrentStep('style')
      setTemplateLandingStep(null)
    }
  }, [currentChart, data, templateLandingStep])

  useEffect(() => {
    if (dataSource?.datasetTitle) {
      setBrandTheme((prev) => ({
        ...prev,
        title: dataSource.datasetTitle,
        subtitle: dataSource.datasetSrc ? `Source: ${dataSource.datasetSrc}` : prev.subtitle,
      }))
    }
  }, [dataSource, setBrandTheme])

  const handleStartBlank = useCallback(() => {
    setShowIntro(false)
    setCurrentStep('load')
  }, [])

  const handleMapChartSelection = useCallback(
    (chart) => {
      handleChartChange(chart)
      setActiveMapTab('mapping')
    },
    [handleChartChange]
  )

  const handleTemplateProjectLoad = useCallback(
    async (project) => {
      setTemplateLandingStep('style')
      setCurrentStep('style')
      await importProject(project)
      setShowIntro(false)
      setActiveStyleTab('brand')
      setCurrentStep('style')
    },
    [importProject]
  )

  return (
    <UIContext.Provider
      value={{
        currentStep,
        setCurrentStep,
        showIntro,
        setShowIntro,
        activeMapTab,
        setActiveMapTab,
        activeStyleTab,
        setActiveStyleTab,
        previewSizePreset,
        setPreviewSizePreset,
        previewCustomSize,
        setPreviewCustomSize,
        previewSizePresets: PREVIEW_SIZE_PRESETS,
        templateLandingStep,
        setTemplateLandingStep,
        mappingComplete,
        workflowSteps,
        canAdvance,
        goToNextStep,
        goToPreviousStep,
        previewRendererMode,
        previewDimensions,
        recommendations,
        aiResult,
        handleRecommendationSelect,
        handleStartBlank,
        handleMapChartSelection,
        handleTemplateProjectLoad,
      }}
    >
      {children}
    </UIContext.Provider>
  )
}

export function useUIContext() {
  const ctx = useContext(UIContext)
  if (!ctx) throw new Error('useUIContext must be used within UIProvider')
  return ctx
}
